import { Loader2 } from 'lucide-react';
import React from 'react';
import { match } from 'ts-pattern';

import { useTaskProcessor } from '@eluve/appointment-tasks';
import { Box, NewButton } from '@eluve/components';
import { useSummary } from '@eluve/frontend-appointment-hooks';
import { useCaptureEvent } from '@eluve/posthog-react';
import { FeatureFlaggedComponent } from '@eluve/smart-blocks';

import { useAppointmentDialogs } from '../AppointmentDialogProvider';
import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../machines/transcription/hooks/useTranscriptionMachine';

import { UploadAudioFileButton } from './UploadAudioFileButton';

interface AppointmentTranscriptionInSessionControlsProps {
  onEndSession?: () => void;
}

export const AppointmentTranscriptionInSessionControls: React.FC<
  AppointmentTranscriptionInSessionControlsProps
> = ({ onEndSession }) => {
  // TODO(deep): [ELU-2758] Use data from cache to determine if summary is available
  const { isSummaryAvailable } = useSummary();
  const appointmentId = useTranscriptionMachineSelector(
    (state) => state.context.appointmentId ?? '',
  );

  const { openOverwriteSummaryDialog } = useAppointmentDialogs();

  const { taskProcessor } = useTaskProcessor();

  const captureEvent = useCaptureEvent({
    appointmentId,
  });

  const state = useTranscriptionMachineSelector((state) => {
    if (
      state.matches({
        InSession: {
          Recording: {
            MediaRecorder: 'Loading',
          },
        },
      })
    ) {
      return 'Loading';
    }

    if (
      state.matches({
        InSession: { Recording: { FinalizingRecording: 'Finalizing' } },
      })
    ) {
      if (state.context.isPausing) {
        return 'FinalizingPause';
      }

      return 'FinalizingStop';
    }

    if (
      state.matches({
        InSession: 'Recording',
      })
    ) {
      return 'Recording';
    }

    if (
      state.matches({
        InSession: 'Paused',
      })
    ) {
      return 'Paused';
    }
  });

  const { send } = useTranscriptionMachineActor();

  const endSession = () => {
    captureEvent('ended_session');
    send({ type: 'STOP' });
    onEndSession?.();

    if (isSummaryAvailable) {
      openOverwriteSummaryDialog(appointmentId);
    } else {
      taskProcessor.addTask({
        type: 'TASK.GENERATE_NOTE',
        appointmentId,
        userCompletedAt: new Date().toISOString(),
      });
    }
  };

  return match(state)
    .with('Recording', 'FinalizingPause', 'FinalizingStop', () => (
      <Box hStack>
        <NewButton
          onClick={() => {
            captureEvent('paused_session');
            send({ type: 'PAUSE' });
          }}
          disabled={state === 'FinalizingStop'}
          loading={state === 'FinalizingPause'}
          type="subtle"
          text="Pause"
        />

        <NewButton
          text="Generate Note"
          data-accessibility-label="generate-note-button"
          disabled={state === 'FinalizingPause'}
          loading={state === 'FinalizingStop'}
          onClick={endSession}
        />
      </Box>
    ))
    .with('Paused', () => (
      <Box hStack>
        <FeatureFlaggedComponent flag="ALLOW_AUDIO_FILE_UPLOAD_FOR_APPOINTMENT">
          <UploadAudioFileButton appointmentId={appointmentId} buttonSize="m" />
        </FeatureFlaggedComponent>
        <NewButton
          onClick={() => {
            captureEvent('resumed_session');
            send({ type: 'RESUME' });
          }}
          type="subtle"
          text="Resume"
        />
        <NewButton
          text="Generate Note"
          data-accessibility-label="generate-note-button"
          onClick={endSession}
        />
      </Box>
    ))
    .with('Loading', () => (
      <Box hStack>
        <Loader2 className="mr-2 h-5 animate-spin text-bg-accent-strong-primary" />
        Processing
      </Box>
    ))
    .otherwise(() => null);
};
